import type { ComboBoxOption } from '@gokrew/ui';
import type { CompanyRole } from '@/types/roles';

/** `errorCode` of the server's refusal by the role ceiling (D207, D208). */
export const ROLE_ABOVE_CALLER = 'ROLE_ABOVE_CALLER';
/** `errorCode` of a change to the company's owner account by someone else (D208). */
export const OWNER_ACCOUNT_PROTECTED = 'OWNER_ACCOUNT_PROTECTED';

/** A 403 the portal shows (other 403s stay silent): the ceiling's refusals. */
export function isCeilingRefusal(errorCode: unknown): boolean {
  return (
    errorCode === ROLE_ABOVE_CALLER || errorCode === OWNER_ACCOUNT_PROTECTED
  );
}

/**
 * Role picker options (Add an administrator, an admin's edit form): a role
 * with permissions the caller does not hold is listed but cannot be picked,
 * with `aboveYouHint` under its name. The server says which (`grantable`)
 * and refuses it anyway.
 */
export function roleOptions(
  roles: CompanyRole[] | undefined,
  aboveYouHint: string
): ComboBoxOption[] {
  return (roles ?? []).map((role) => ({
    value: role.id,
    label: role.name,
    ...(role.grantable === false
      ? { disabled: true, description: aboveYouHint }
      : {}),
  }));
}

/**
 * The role editor's ceiling: a permission the caller does not hold cannot be
 * ticked on a role (unticking stays free). `held` is the caller's own scopes.
 */
export function isAboveCaller(
  scope: string,
  checked: boolean,
  held: ReadonlySet<string>
): boolean {
  return !checked && !held.has(scope);
}

/**
 * Why the caller may not change this administrator (D208), as the server says
 * on each row: null when they may. The routes refuse anyway.
 */
export function adminLockReason(admin: {
  manageable?: boolean;
  isOwnerAccount?: boolean;
}): 'owner_account' | 'above_you' | null {
  if (admin.manageable !== false) return null;
  return admin.isOwnerAccount ? 'owner_account' : 'above_you';
}

/**
 * A role the caller may not edit (D208): it holds a permission they do not.
 * `assignedScopes` are the role's assignable permissions.
 */
export function isRoleAboveCaller(
  assignedScopes: string[],
  held: ReadonlySet<string>
): boolean {
  return assignedScopes.some((scope) => !held.has(scope));
}

/**
 * Administrator picker options on the location screens (D208): someone the
 * caller may not change (above them, or the owner account) is listed but
 * cannot be picked, with the reason under their name.
 */
export function lockAdminOptions<
  T extends { value: string; disabled?: boolean; description?: string },
>(
  options: T[],
  admins:
    | { id: string; manageable?: boolean; isOwnerAccount?: boolean }[]
    | undefined,
  hints: { aboveYou: string; ownerAccount: string }
): T[] {
  const reasons = new Map(
    (admins ?? []).map((admin) => [admin.id, adminLockReason(admin)])
  );
  return options.map((option) => {
    const reason = reasons.get(option.value);
    if (!reason) return option;
    return {
      ...option,
      disabled: true,
      description:
        reason === 'owner_account' ? hints.ownerAccount : hints.aboveYou,
    };
  });
}
